import React, { useEffect, useState } from 'react';

import {
    AdjustmentModel,
    ContractTermsEnum,
    GetAdjustmentsInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { isEmpty, isUndefined } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { AppContext } from 'src/contexts/appContext';
import { IContractTermsComponentProps } from 'src/types/IRateCard';

export const GrowthTarget = ({
    contractTerms,
    isEditMode,
    handleContractTermValueChange
}: IContractTermsComponentProps) => {
    const [flatDiscountAdjustmentModel, setFlatDiscountAdjustmentModel] = useState<AdjustmentModel>({});
    const { setTopAlertMsg } = React.useContext(AppContext);

    const getFlatDiscountAdjustmentModelDefValue = (): string | undefined => {
        const defValue = flatDiscountAdjustmentModel.adjustmentModelDefinitions?.[0].value;
        return !isUndefined(defValue) ? defValue : undefined;
    };

    const handleHasGrowthTargetChange = (newVal: boolean) => {
        handleContractTermValueChange(
            'flatDiscount',
            '',
            newVal ? getFlatDiscountAdjustmentModelDefValue() : undefined
        );

        /* 
            If Growth Target is being toggled on AND UMG Perf Disc is already enabled,
            toggle off the UMG Perf Disc
        */
        if (newVal && !isEmpty(contractTerms.performanceDiscounts)) {
            handleContractTermValueChange('performanceDiscounts', '', []);
        }
    };

    useEffect(() => {
        if (isEditMode) {
            contractConfigurationClient
                .getAdjustments(
                    GetAdjustmentsInput.clone({
                        adjustmentModel: ContractTermsEnum.FLAT_RATE_DISCOUNT
                    })
                )
                .toPromise()
                .then((response) => {
                    if (!isEmpty(response.adjustmentModels)) {
                        setFlatDiscountAdjustmentModel(response.adjustmentModels?.[0] ?? {});
                    }
                })
                .catch((error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('GetAdjustments API Unexpected error: ', error.receivedException?.message);
                    setTopAlertMsg(error.receivedException?.message);
                });
        }
    }, []);

    return (
        <Box className="box-border-light-gray" type="outline" spacingInset="400">
            <Column>
                <Row spacing="100" alignmentHorizontal="justify">
                    <Row spacing="none">
                        <Heading level={3}>UMG growth target</Heading>
                        <ContractTermInfoButton contractTermName={ContractTermName.UMGGrowthTarget} />
                    </Row>
                    {isEditMode && (
                        <Toggle
                            label={''}
                            value={!isUndefined(contractTerms.flatDiscount)}
                            isEditMode={isEditMode}
                            onChange={handleHasGrowthTargetChange}
                            disabled={isUndefined(getFlatDiscountAdjustmentModelDefValue())}
                        />
                    )}
                </Row>
                {isUndefined(contractTerms.flatDiscount) ? (
                    <Row>
                        <Toggle label="Enable revenue discount percentage" value={false} isEditMode={false} />
                    </Row>
                ) : (
                    <Row alignmentVertical="top" widths="50%">
                        <Column spacing="100">
                            <Text type="b200">Revenue discount percentage</Text>
                            <Text type="b300">{contractTerms.flatDiscount}</Text>
                        </Column>
                    </Row>
                )}
            </Column>
        </Box>
    );
};
